import { Box, Input, InputGroup, InputLeftElement } from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from 'src/components/atoms/Icon';
import { DataBox, DataBoxBaseProps } from 'src/components/molecules/DataBox';
import { DataTable } from 'src/components/molecules/DataTable';
import { PaginationControls } from 'src/components/molecules/PaginationControls';
import { useSelectedDimensionContext } from 'src/features/MM/contexts/SelectedDimensionContext';
import { getDimensionName } from 'src/features/MM/utils/getDimensionName';
import { SummaryInfoAlert } from './SummaryInfoAlert';
import { useColumns, useTableData } from './useTableData';

const DEFAULT_PAGE_SIZE = 25;

export const SummaryTableBox: React.FC<DataBoxBaseProps> = (props) => {
  const { dimension } = useSelectedDimensionContext();

  const [infoOpen, setInfoOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [displayedCount, setDisplayedCount] = useState(DEFAULT_PAGE_SIZE);

  const { data, isError, isLoading } = useTableData();

  const columns = useColumns();

  const dimensionName = getDimensionName(dimension);

  const filteredData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) => row.dimension.toLowerCase().includes(q));
  }, [data, searchQuery]);

  const paginatedData = useMemo(
    () => filteredData.slice(0, displayedCount),
    [filteredData, displayedCount],
  );

  useEffect(() => {
    setDisplayedCount(pageSize);
  }, [pageSize, searchQuery, dimension]);

  const hasMore = displayedCount < filteredData.length;

  return (
    <>
      <SummaryInfoAlert
        infoOpen={infoOpen}
        onClose={() => setInfoOpen(false)}
      />
      <DataBox
        {...props}
        onInfoButtonClick={() => setInfoOpen(true)}
        exportEnabled
        hasData={data.length > 0}
        isError={isError}
        tableBox
        isLoading={isLoading}>
        <Box px={6} pt={4} pb={2}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Icon iconName="search" boxSize={4} color="content.muted" />
            </InputLeftElement>
            <Input
              placeholder={`Search ${dimensionName.toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              border="1px solid"
              borderColor="surface.border"
              borderRadius="md"
              pl={10}
              fontFamily="marketingPage"
              _placeholder={{ color: 'content.muted' }}
              _focus={{
                borderColor: 'icon.green',
                boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
              }}
            />
          </InputGroup>
        </Box>

        <DataTable
          csvFileName={dimensionName}
          data={paginatedData}
          exportData={data}
          columns={columns}
        />

        {filteredData.length > 0 && (
          <Box position="sticky" bottom={3} width="fit-content" mx="auto">
            <PaginationControls
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalItems={filteredData.length}
              displayedCount={paginatedData.length}
              onShowMore={() => setDisplayedCount((c) => c + pageSize)}
              hasMore={hasMore}
              itemLabel={dimensionName}
            />
          </Box>
        )}
      </DataBox>
    </>
  );
};
